import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Users, Clock, Phone, Globe, Building2, RefreshCw, BadgeCheck, UserCheck, BellRing, UtensilsCrossed, LogIn, Check, X, Timer, UserX, History,
} from 'lucide-react';
import { useT } from '../context/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { useToast } from '../components/ui/use-toast';
import { confirm } from '../components/ui/confirm-dialog';
import { TimePicker } from '../components/ui/time-picker';
import { useRestaurant } from '../context/RestaurantContext';
import { useAppSettings } from '../context/AppSettingsContext';
import apiClient from '../services/api';

/** All Reservations: every future booking, soonest first, grouped by day, with source, guest and approver. */

// How the booking was made, as a badge; ONLINE reads "Website", which is what staff actually call it.
const SOURCE_BADGES = {
  ONLINE:    { label: 'Website',   Icon: Globe,     cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  PHONE:     { label: 'Phone',     Icon: Phone,     cls: 'bg-violet-50 text-violet-700 border-violet-200' },
  RECEPTION: { label: 'Reception', Icon: Building2, cls: 'bg-slate-100 text-slate-700 border-slate-200' },
};

const STATUS_CLS = {
  BOOKED:    'bg-amber-100 text-amber-800',
  // On the payment clock: blocked, not yet booked — grey, so it reads as neither free nor confirmed.
  HELD:      'bg-slate-200 text-slate-800',
  CONFIRMED: 'bg-emerald-100 text-emerald-800',
  SEATED:    'bg-blue-100 text-blue-800',
  COMPLETED: 'bg-slate-100 text-slate-600',
  CANCELLED: 'bg-red-100 text-red-700',
  NO_SHOW:   'bg-red-50 text-red-500',
};

/* How the money already taken on a booking reads: the figure never changes,
   what changes is whose money it is now — still owed to the guest, spent on
   their bill, or gone back to them. */
const ADVANCE_STATES = {
  PENDING:   { note: 'Not paid yet',        cls: 'text-amber-700' },
  PAID:      { note: 'Held for the bill',   cls: 'text-emerald-700' },
  APPLIED:   { note: 'Adjusted in bill',    cls: 'text-sky-700' },
  REFUNDED:  { note: 'Refunded',            cls: 'text-muted-foreground line-through' },
  FORFEITED: { note: 'Forfeited',           cls: 'text-red-600' },
};

/* One column heading built by the same component, so labels share a baseline whether or not a filter sits below. */
function Th({ label, className = '', align = 'left', children }) {
  const alignCls = align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
  return (
    <TableHead className={`${alignCls} ${className}`}>
      <div className="py-1.5">
        <div className="h-5 leading-5 whitespace-nowrap">{label}</div>
        {children && <div className="mt-1">{children}</div>}
      </div>
    </TableHead>
  );
}

// The booking's audit trail — every staff decision, worded for the floor.
const AUDIT_LABEL = {
  APPROVED:        'Approved',
  DECLINED:        'Declined',
  CANCELLED:       'Cancelled',
  AUTO_CANCELLED:  'Auto-cancelled — hold ran out',
  NO_SHOW:         'Marked no-show',
  HOLD_EXTENDED:   'Hold extended',
  RESCHEDULED:     'Time changed',
  ADVANCE_APPLIED: 'Advance adjusted in bill',
  CHECKED_IN:      'Checked in',
};
const AUDIT_CLS = {
  APPROVED:        'bg-emerald-100 text-emerald-800',
  DECLINED:        'bg-red-100 text-red-700',
  CANCELLED:       'bg-red-100 text-red-700',
  AUTO_CANCELLED:  'bg-red-100 text-red-700',
  PAYMENT_EXPIRED: 'bg-red-100 text-red-700',
  BLACKLISTED:     'bg-slate-800 text-white',
  NO_SHOW:         'bg-red-50 text-red-600',
  HOLD_EXTENDED:   'bg-sky-100 text-sky-800',
  RESCHEDULED:     'bg-violet-100 text-violet-800',
  ADVANCE_APPLIED: 'bg-emerald-50 text-emerald-700',
  CHECKED_IN:      'bg-blue-100 text-blue-800',
};

// Check-in opens this many minutes before the booked time; earlier than that, staff move the booking instead.
const EARLY_CHECKIN_MINUTES = 15;

// An ISO stamp as a value for <input type="datetime-local"> (local wall time).
const toLocalInput = (iso) => {
  const d = iso ? new Date(iso) : new Date();
  if (Number.isNaN(d.getTime())) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

export default function AllReservations() {
  const { t } = useT();
  const { restaurantId } = useRestaurant();
  const { formatCurrency } = useAppSettings();
  const { toast } = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // ?status=BOOKED opens straight on the requests awaiting a decision, where the bell and toasts send staff.
  const [params] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState(() => params.get('status') || 'all');
  const [sourceFilter, setSourceFilter] = useState('all');
  /* What the guest is coming FOR, as opposed to where the booking came
     from — a buffet party and an a-la-carte table are prepped, priced and
     seated differently, so it is the column staff scan for. */
  const [diningFilter, setDiningFilter] = useState('all');
  // Which day to show: 'all', 'today', 'tomorrow', or a picked 'YYYY-MM-DD'.
  const [dayFilter, setDayFilter] = useState('all');
  const [checkingInId, setCheckingInId] = useState(null);
  const [decidingId, setDecidingId] = useState(null);
  // Reseating is opt-in per row, so the free-table list is fetched only when someone opens the picker.
  const [seatChoice, setSeatChoice] = useState({});
  const [seatTables, setSeatTables] = useState({});
  const [seatLoading, setSeatLoading] = useState(null);
  /* The row whose extend-hold panel is open: one at a time, since extending is a decision about one table. */
  const [extendOpenId, setExtendOpenId] = useState(null);
  const [customMins, setCustomMins] = useState('');
  const [extendBusy, setExtendBusy] = useState(false);
  /* The row whose change-time panel is open; moving a booking is how an early guest gets seated. */
  const [timeOpenId, setTimeOpenId] = useState(null);
  const [newTime, setNewTime] = useState('');
  const [timeBusy, setTimeBusy] = useState(false);
  /* Per-booking audit trail: which row is open, and the fetched entries so reopening doesn't refetch. */
  const [auditOpenId, setAuditOpenId] = useState(null);
  const [audit, setAudit] = useState({});
  const [auditLoading, setAuditLoading] = useState(null);

  // A decision changed the booking — any cached trail for it is now stale.
  const dropAuditCache = (id) =>
    setAudit((p) => { const c = { ...p }; delete c[id]; return c; });

  const openSeatPicker = async (r) => {
    if (seatTables[r.id]) return;           // already loaded
    setSeatLoading(r.id);
    try {
      const { data } = await apiClient.get(
        `/users/restaurant/${restaurantId}/reservations/available-tables/`,
        { params: { reservation_id: r.id } });
      setSeatTables((p) => ({ ...p, [r.id]: data?.results || [] }));
    } catch (e) {
      toast({
        title: 'Could not load free tables',
        description: e?.response?.data?.error || e.message,
        variant: 'destructive',
      });
    } finally {
      setSeatLoading(null);
    }
  };

  // Approve / decline a pending request from this list; approve keeps the requested table unless the picker says otherwise.
  const decide = async (r, action) => {
    if (action === 'decline' || action === 'cancel') {
      const ok = await confirm({
        title: action === 'decline' ? 'Decline this reservation?' : 'Cancel this reservation?',
        description: `${r.customer_name || 'Guest'} · ${r.pax || '?'} pax — the booking will be cancelled and the guest loses the table.`,
        confirmText: action === 'decline' ? 'Decline booking' : 'Cancel booking',
        tone: 'destructive',
      });
      if (!ok) return;
    }
    if (action === 'no-show') {
      const ok = await confirm({
        title: 'Mark as a no-show?',
        description: `${r.customer_name || 'Guest'} · ${r.pax || '?'} pax — the table is released and the no-show `
          + "is recorded on the guest's profile and in the booking's history. "
          + 'Use Cancel instead if they called to say they weren’t coming.',
        confirmText: 'Mark no-show',
        tone: 'destructive',
      });
      if (!ok) return;
    }
    setDecidingId(r.id);
    try {
      const choice = seatChoice[r.id] || '';
      const body = action === 'approve'
        ? (choice === 'random' ? { seating: 'random' }
          : choice ? { table_id: Number(choice) } : {})
        : {};
      const { data } = await apiClient.post(
        `/users/restaurant/${restaurantId}/reservations/${r.id}/${action}/`, body);
      if (!data?.success) throw new Error(data?.error || 'Failed');
      toast({
        title: action === 'approve' ? 'Reservation approved'
          : action === 'cancel' ? 'Reservation cancelled'
          : action === 'no-show' ? 'Marked as a no-show' : 'Reservation declined',
        description: `${r.customer_name || 'Guest'} · ${r.pax || '?'} pax`,
      });
      dropAuditCache(r.id);
      load();
    } catch (e) {
      toast({ title: 'Error', description: e?.response?.data?.error || e.message, variant: 'destructive' });
    } finally {
      setDecidingId(null);
    }
  };

  // The guest rang to say they're late: cumulative per booking, and every grant lands in its history.
  const extendHold = async (r, minutes) => {
    const mins = Number(minutes);
    if (!mins || mins <= 0) return;
    setExtendBusy(true);
    try {
      const { data } = await apiClient.post(
        `/users/restaurant/${restaurantId}/reservations/${r.id}/extend-hold/`,
        { minutes: mins });
      if (!data?.success) throw new Error(data?.error || 'Failed');
      toast({
        title: `Hold extended by ${mins} min`,
        description: data.data?.held_until
          ? `Table held until ${time(data.data.held_until)}` : undefined,
      });
      setExtendOpenId(null);
      setCustomMins('');
      dropAuditCache(r.id);
      load();
    } catch (e) {
      toast({ title: 'Could not extend the hold', description: e?.response?.data?.error || e.message, variant: 'destructive' });
    } finally {
      setExtendBusy(false);
    }
  };

  // Move the booking to a new time - the recorded way to seat an early guest without cancel-and-rebook.
  const reschedule = async (r) => {
    const dt = newTime ? new Date(newTime) : null;
    if (!dt || Number.isNaN(dt.getTime())) return;
    setTimeBusy(true);
    try {
      const { data } = await apiClient.post(
        `/users/restaurant/${restaurantId}/reservations/${r.id}/reschedule/`,
        { reserved_start_at: dt.toISOString() });
      if (!data?.success) throw new Error(data?.error || 'Failed');
      toast({
        title: 'Booking time changed',
        description: `${r.customer_name || 'Guest'} — now at ${time(data.data?.reserved_start_at)}`,
      });
      setTimeOpenId(null);
      setNewTime('');
      dropAuditCache(r.id);
      load();
    } catch (e) {
      toast({ title: 'Could not change the time', description: e?.response?.data?.error || e.message, variant: 'destructive' });
    } finally {
      setTimeBusy(false);
    }
  };

  const toggleAudit = async (r) => {
    if (auditOpenId === r.id) { setAuditOpenId(null); return; }
    setAuditOpenId(r.id);
    if (audit[r.id]) return;                 // already fetched
    setAuditLoading(r.id);
    try {
      const { data } = await apiClient.get(
        `/users/restaurant/${restaurantId}/reservations/${r.id}/audit/`);
      setAudit((p) => ({ ...p, [r.id]: data?.results || [] }));
    } catch {
      setAudit((p) => ({ ...p, [r.id]: [] }));
    } finally {
      setAuditLoading(null);
    }
  };

  // One line of context per audit entry, beyond its label.
  const auditDetail = (e) => {
    const d = e.details || {};
    if (e.event === 'HOLD_EXTENDED') {
      return `+${d.added_minutes} min${d.held_until ? ` — held until ${time(d.held_until)}` : ''}`;
    }
    if (e.event === 'NO_SHOW' && d.released_table) return `table ${d.released_table} released`;
    if (e.event === 'APPROVED' && d.table) return `table ${d.table}`;
    if (e.event === 'AUTO_CANCELLED' && d.held_until) return `table was held until ${time(d.held_until)}`;
    if (e.event === 'RESCHEDULED' && d.to_time) {
      const stamp = (iso) => new Date(iso).toLocaleString([], {
        day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
      });
      return `${d.from_time ? `${stamp(d.from_time)} → ` : ''}${stamp(d.to_time)}`;
    }
    return '';
  };

  // Guest walked in: seat them and fire any saved pre-order straight to the kitchen.
  const checkIn = async (r) => {
    setCheckingInId(r.id);
    try {
      const { data } = await apiClient.post(
        `/users/restaurant/${restaurantId}/reservations/${r.id}/arrived/`);
      if (!data?.success) throw new Error(data?.error || 'Check-in failed');
      toast({ title: 'Guest checked in', description: data.message });
      load();
    } catch (e) {
      toast({
        title: 'Check-in failed',
        description: e?.response?.data?.error || e.message,
        variant: 'destructive',
      });
    } finally {
      setCheckingInId(null);
    }
  };

  const load = useCallback(async () => {
    if (!restaurantId) return;
    setLoading(true);
    try {
      const { data } = await apiClient.get(`/users/restaurant/${restaurantId}/reservations/upcoming/`);
      if (!data?.success) throw new Error(data?.error || 'Failed to load reservations');
      setRows(data.results || []);
      setError('');
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [restaurantId]);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  // Arriving from the bell doesn't remount the page, so follow the URL, keyed on the value so filter clicks stick.
  const urlStatus = params.get('status');
  useEffect(() => {
    if (urlStatus) setStatusFilter(urlStatus);
  }, [urlStatus]);

  // Local calendar day as 'YYYY-MM-DD' — same shape the grouping key uses.
  const localDay = (offsetDays = 0) => {
    const d = new Date();
    d.setDate(d.getDate() + offsetDays);
    return [d.getFullYear(), String(d.getMonth() + 1).padStart(2, '0'),
      String(d.getDate()).padStart(2, '0')].join('-');
  };
  const dayKey = dayFilter === 'today' ? localDay()
    : dayFilter === 'tomorrow' ? localDay(1) : dayFilter;

  const filtered = rows.filter((r) => {
    if (statusFilter !== 'all' && r.reservation_status !== statusFilter) return false;
    if (sourceFilter !== 'all' && (r.booking_source || 'RECEPTION') !== sourceFilter) return false;
    if (diningFilter !== 'all' && (r.dining_type || 'ALACARTE') !== diningFilter) return false;
    if (dayFilter !== 'all' && (r.reserved_start_at || '').slice(0, 10) !== dayKey) return false;
    return true;
  });

  // Group by calendar day, keeping the soonest-first order inside each group.
  const groups = useMemo(() => {
    const map = new Map();
    filtered.forEach((r) => {
      const key = r.reserved_start_at ? r.reserved_start_at.slice(0, 10) : '—';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    });
    return Array.from(map.entries());
  }, [filtered]);

  const dayLabel = (isoDay) => {
    if (isoDay === '—') return 'Unscheduled';
    const d = new Date(`${isoDay}T00:00:00`);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const diff = Math.round((d - today) / 864e5);
    const base = d.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' });
    if (diff === 0) return `Today · ${base}`;
    if (diff === 1) return `Tomorrow · ${base}`;
    return base;
  };

  const time = (iso) =>
    iso ? new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—';

  /* A column-header filter, outlined in the accent when active so a narrowed list never looks like an empty one. */
  const filterCls = (active) =>
    `h-7 w-full rounded-md border bg-background px-1.5 text-[11px] font-normal cursor-pointer
     focus:outline-none focus:ring-2 focus:ring-ring/30 ${
      active
        ? 'border-primary text-foreground font-medium'
        : 'border-input text-muted-foreground hover:text-foreground'
    }`;

  return (
    <div className="p-6 w-full">
      <header className="mb-6 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight leading-none">All Reservations</h1>
        </div>
        {/* The day control sits beside Refresh, since it governs the whole table rather than one column's values. */}
        <div className="flex items-end gap-2">
          <label className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">Day</span>
            <select
              value={['all', 'today', 'tomorrow'].includes(dayFilter) ? dayFilter : 'custom'}
              onChange={(e) => setDayFilter(e.target.value === 'custom' ? localDay() : e.target.value)}
              title="Show only bookings for this day"
              className={`h-9 rounded-md border bg-white px-2.5 text-sm cursor-pointer
                focus:outline-none focus:ring-2 focus:ring-ring/30 ${
                dayFilter !== 'all'
                  ? 'border-primary text-foreground font-medium'
                  : 'border-border text-slate-700'
              }`}
            >
              <option value="all">All days</option>
              <option value="today">Today</option>
              <option value="tomorrow">Tomorrow</option>
              <option value="custom">Pick a date…</option>
            </select>
          </label>
          {!['all', 'today', 'tomorrow'].includes(dayFilter) && (
            <input
              type="date"
              value={dayFilter}
              onChange={(e) => setDayFilter(e.target.value || 'all')}
              title="The day to show"
              className="h-9 rounded-md border border-primary bg-white px-2.5 text-sm text-foreground
                focus:outline-none focus:ring-2 focus:ring-ring/30"
            />
          )}
          <button
            onClick={load}
            className="inline-flex items-center gap-2 rounded-md border border-border bg-white px-4 py-2 h-9 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </header>

      {error && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-700">{error}</div>
      )}

      {loading && rows.length === 0 ? (
        <p className="text-sm text-muted-foreground py-10 text-center">Loading reservations…</p>
      ) : rows.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <BellRing className="h-10 w-10 mx-auto mb-3 opacity-30" />
            No upcoming reservations.
          </CardContent>
        </Card>
      ) : (
        /* A ledger, not cards: one table for every day, with variable-height content on full-width sub-rows. */
        <Card className="overflow-hidden">
          {/* Narrow screens scroll the table rather than reflowing it - a column that wraps under another is not a column. */}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                {/* Every heading is built by the same Th, so labels start on one line whatever control sits under them. */}
                <TableRow className="hover:bg-transparent bg-secondary/40 align-top">
                  {/* No day picker here: the day is a page control, while Status and Source narrow values within their columns. */}
                  <Th label="Time" className="w-[104px]" />
                  <Th label="Guest" className="min-w-[150px]" />
                  <Th label="Request type" className="w-[150px]">
                    <select
                      value={diningFilter}
                      onChange={(e) => setDiningFilter(e.target.value)}
                      title="Show only bookings for this kind of visit"
                      className={filterCls(diningFilter !== 'all')}
                    >
                      {[['all', 'All types'], ['ALACARTE', 'A la carte'],
                        ['BUFFET', 'Buffet']].map(([k, l]) => (
                        <option key={k} value={k}>{l}</option>
                      ))}
                    </select>
                  </Th>
                  <Th label="Phone" className="w-[118px]" />
                  <Th label="Pax" className="w-[62px]" align="center" />
                  <Th label="Table" className="min-w-[136px]" />
                  {/* The filter sits in the column it filters, so the control and the data it governs are the same column. */}
                  <Th label="Status" className="min-w-[172px]">
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      title="Show only bookings with this status"
                      className={filterCls(statusFilter !== 'all')}
                    >
                      {[['all', 'All statuses'], ['BOOKED', 'Awaiting approval'], ['CONFIRMED', 'Confirmed'],
                        ['SEATED', 'Seated'], ['CANCELLED', 'Cancelled'], ['NO_SHOW', 'No-shows']].map(([k, l]) => (
                        <option key={k} value={k}>{l}</option>
                      ))}
                    </select>
                  </Th>
                  {/* Money gets its own column: the Status badge says a booking is
                      paid, only a figure says how much came in. */}
                  <Th label="Advance received" className="w-[136px]" align="right" />
                  <Th label="Source" className="w-[130px]">
                    <select
                      value={sourceFilter}
                      onChange={(e) => setSourceFilter(e.target.value)}
                      title="Show only bookings that came in this way"
                      className={filterCls(sourceFilter !== 'all')}
                    >
                      {[['all', 'All sources'], ['ONLINE', 'Website'],
                        ['RECEPTION', 'Reception'], ['PHONE', 'Phone']].map(([k, l]) => (
                        <option key={k} value={k}>{l}</option>
                      ))}
                    </select>
                  </Th>
                  <Th label="Requested at" className="w-[126px]" />
                  <Th label="Approved / Created by" className="min-w-[170px]" />
                  <Th label="Actions" className="min-w-[260px]" align="right" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {/* Filters live in the header, so an empty result must keep the table and its controls on screen. */}
                {groups.length === 0 && (
                  <TableRow className="hover:bg-transparent">
                    <TableCell colSpan={12} className="py-10 text-center text-muted-foreground">
                      No bookings match these filters.
                    </TableCell>
                  </TableRow>
                )}
                {groups.map(([day, list]) => (
                  <React.Fragment key={day}>
                    {/* The day, as a band across the grid rather than a heading between separate tables. */}
                    <TableRow className="hover:bg-transparent bg-muted/50">
                      <TableCell colSpan={12} className="py-2">
                        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                          {dayLabel(day)}
                        </span>
                        <span className="text-xs text-slate-400"> · {list.length} booking(s)</span>
                      </TableCell>
                    </TableRow>
                    {list.map((r) => {
                        const src = SOURCE_BADGES[r.booking_source] || SOURCE_BADGES.RECEPTION;
                        const SrcIcon = src.Icon;
                        const hasSub = r.notes || extendOpenId === r.id || auditOpenId === r.id
                          || timeOpenId === r.id;
                        /* Check-in only opens shortly before the booked time; greying it here just says so before the tap. */
                        const opensAt = r.reserved_start_at
                          ? new Date(new Date(r.reserved_start_at).getTime() - EARLY_CHECKIN_MINUTES * 60000)
                          : null;
                        const tooEarly = opensAt && new Date() < opensAt;
                        /* Extend hold and No show only appear from the booked minute, since neither means anything before it. */
                        const started = r.reserved_start_at
                          ? new Date() >= new Date(r.reserved_start_at)
                          : true;
                        return (
                          <React.Fragment key={r.id}>
                            <TableRow className={`align-top ${hasSub ? 'border-b-0' : ''}`}>
                              {/* Time, and on a live booking the way to move it - changing WHEN belongs under the time. */}
                              <TableCell className="py-3">
                                <div className="font-bold text-foreground leading-tight">{time(r.reserved_start_at)}</div>
                                {['BOOKED', 'CONFIRMED'].includes(r.reservation_status) && (
                                  <div className="mt-1">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setTimeOpenId(timeOpenId === r.id ? null : r.id);
                                        setNewTime(toLocalInput(r.reserved_start_at));
                                      }}
                                      className="text-xs font-medium text-primary underline underline-offset-2 hover:opacity-80"
                                      title="Move this booking to another time — recorded in its history"
                                    >
                                      Change time
                                    </button>
                                  </div>
                                )}
                              </TableCell>

                              {/* Guest — name on top, the booking code under it */}
                              <TableCell className="py-3">
                                {r.customer_id ? (
                                  <Link
                                    to={`/crm?customer=${r.customer_id}&from=reservations`}
                                    className="font-semibold text-primary hover:underline"
                                    title="Open customer profile (CRM)"
                                  >
                                    {r.customer_name || 'Guest'}
                                  </Link>
                                ) : (
                                  <span className="font-semibold text-foreground">{r.customer_name || 'Guest'}</span>
                                )}
                                {r.reservation_code && (
                                  <div className="text-[11px] text-muted-foreground font-mono">{r.reservation_code}</div>
                                )}
                              </TableCell>

                              {/* What the guest is coming FOR. Its own column rather than a
                                  badge inside Status: it is not a state the booking passes
                                  through, it is what the kitchen and the floor prep for, and
                                  it is filtered on. */}
                              <TableCell className="py-3">
                                {r.dining_type === 'BUFFET' ? (
                                  <>
                                    <span className="px-2 py-0.5 text-[11px] rounded-full font-medium bg-orange-100 text-orange-700">
                                      Buffet
                                    </span>
                                    {r.buffet_name && (
                                      <div className="mt-0.5 text-[11px] text-muted-foreground truncate" title={r.buffet_name}>
                                        {r.buffet_name}
                                      </div>
                                    )}
                                    {/* The adult/kid split rides here, since only a buffet
                                        charges and preps children apart. */}
                                    {(Number(r.kids) || 0) > 0 && (
                                      <div className="text-[11px] text-muted-foreground">
                                        {Math.max(0, (Number(r.pax) || 0) - Number(r.kids))}+{Number(r.kids)} kids
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <span className="text-xs text-muted-foreground">A la carte</span>
                                )}
                              </TableCell>

                              {/* Phone gets its own column: the number staff dial to confirm, delay or chase a booking. */}
                              <TableCell className="py-3">
                                {r.customer_phone ? (
                                  <span className="text-xs text-muted-foreground inline-flex items-center gap-1 whitespace-nowrap">
                                    <Phone size={11} /> {r.customer_phone}
                                  </span>
                                ) : (
                                  <span className="text-muted-foreground text-xs">—</span>
                                )}
                              </TableCell>

                              {/* Pax */}
                              <TableCell className="py-3 text-center">
                                <span className="inline-flex items-center gap-1 font-medium text-foreground">
                                  <Users size={12} className="text-muted-foreground" /> {r.pax || '?'}
                                </span>
                              </TableCell>

                              {/* Table, and on a request where to seat them, since choosing the table belongs under the table. */}
                              <TableCell className="py-3">
                                {r.table_code
                                  ? (
                                    <span className="font-medium text-foreground whitespace-nowrap"
                                      title={r.table_capacity
                                        ? `Table ${r.table_code} seats ${r.table_capacity}`
                                        : undefined}>
                                      {r.table_code}
                                      {r.table_capacity ? (
                                        <span className="ml-1 text-xs font-normal text-muted-foreground">
                                          · {r.table_capacity} seats
                                        </span>
                                      ) : null}
                                    </span>
                                  )
                                  : <span className="text-muted-foreground text-xs">—</span>}
                                {r.reservation_status === 'BOOKED' && (
                                  <div className="mt-1">
                                    {seatTables[r.id] ? (
                                      <select
                                        value={seatChoice[r.id] || ''}
                                        onChange={(e) => setSeatChoice((p) => ({ ...p, [r.id]: e.target.value }))}
                                        className="h-7 w-full max-w-[160px] rounded-md border border-input bg-background px-1.5 text-xs"
                                        title="Where to seat this party on approval"
                                      >
                                        <option value="">
                                          {r.table_code
                                            ? `Keep ${r.table_code}${r.table_capacity ? ` · ${r.table_capacity} seats` : ''}`
                                            : 'No table yet'}
                                        </option>
                                        <option value="random">Random free table</option>
                                        {Object.entries(
                                          seatTables[r.id].reduce((groups, t) => {
                                            const label = [t.floor_name, t.section_name].filter(Boolean).join(' · ')
                                              || 'Unassigned area';
                                            (groups[label] = groups[label] || []).push(t);
                                            return groups;
                                          }, {}),
                                        ).map(([label, tbls]) => (
                                          <optgroup key={label} label={label}>
                                            {tbls.map((t) => (
                                              <option key={t.id} value={t.id}>
                                                Table {t.table_code} · {t.capacity} seats
                                              </option>
                                            ))}
                                          </optgroup>
                                        ))}
                                      </select>
                                    ) : (
                                      /* A link until it is needed, so the free-table list is only fetched when someone asks. */
                                      <button
                                        type="button"
                                        disabled={seatLoading === r.id}
                                        onClick={() => openSeatPicker(r)}
                                        className="text-xs font-medium text-primary underline underline-offset-2 hover:opacity-80 disabled:opacity-60"
                                      >
                                        {seatLoading === r.id ? 'Loading…' : 'Change table'}
                                      </button>
                                    )}
                                  </div>
                                )}
                              </TableCell>

                              {/* Status, then the badges that qualify it */}
                              <TableCell className="py-3">
                                <div className="flex flex-wrap gap-1">
                                  <span className={`px-2 py-0.5 text-[11px] rounded-full font-medium ${STATUS_CLS[r.is_held ? 'HELD' : r.reservation_status] || 'bg-secondary'}`}>
                                    {r.is_held ? t('tables.resv.held')
                                      : r.reservation_status === 'BOOKED' ? 'Awaiting approval' : r.reservation_status}
                                  </span>
                                  {r.is_held && r.hold_expires_at && (
                                    <span className="px-2 py-0.5 text-[11px] rounded-full font-medium bg-slate-100 text-slate-600">
                                      {t('tables.resv.heldUntil', { time: time(r.hold_expires_at) })}
                                    </span>
                                  )}
                                  {r.payment_status === 'PAID' && (
                                    <span className="px-2 py-0.5 text-[11px] rounded-full font-medium bg-emerald-50 text-emerald-700">Paid</span>
                                  )}
                                  {r.payment_status === 'APPLIED' && (
                                    <span
                                      className="px-2 py-0.5 text-[11px] rounded-full font-medium bg-sky-50 text-sky-700"
                                      title={`Booking amount deducted from the bill${r.payment_applied_order_number ? ` (order ${r.payment_applied_order_number})` : ''}`}
                                    >
                                      Adjusted in bill
                                    </span>
                                  )}
                                  {r.has_preorder && !r.order_number && (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded-full font-medium bg-purple-50 text-purple-700"
                                      title="The guest pre-ordered — the kitchen starts it at check-in">
                                      <UtensilsCrossed size={11} /> Pre-order
                                    </span>
                                  )}
                                  {r.order_number && (
                                    <span className="px-2 py-0.5 text-[11px] rounded-full font-medium bg-blue-50 text-blue-700 font-mono"
                                      title="Kitchen order created from this booking at check-in">
                                      Order {r.order_number}
                                    </span>
                                  )}
                                </div>
                              </TableCell>

                              {/* What the guest has already put down on this booking. */}
                              <TableCell className="py-3 text-right">
                                {ADVANCE_STATES[r.payment_status] && Number(r.payment_amount) > 0 ? (
                                  <span
                                    title={r.payment_status === 'APPLIED' && r.payment_applied_order_number
                                      ? `Deducted from order ${r.payment_applied_order_number}`
                                      : undefined}
                                  >
                                    <span className={`block font-semibold whitespace-nowrap ${ADVANCE_STATES[r.payment_status].cls}`}>
                                      {formatCurrency(r.payment_amount)}
                                    </span>
                                    <span className="block text-[11px] text-muted-foreground">
                                      {ADVANCE_STATES[r.payment_status].note}
                                    </span>
                                  </span>
                                ) : (
                                  /* No advance was asked of this booking - a dash, not a zero, since nothing was ever due. */
                                  <span className="text-muted-foreground text-xs">—</span>
                                )}
                              </TableCell>

                              {/* Source */}
                              <TableCell className="py-3">
                                <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${src.cls}`}>
                                  <SrcIcon size={13} /> {src.label}
                                </span>
                              </TableCell>

                              {/* When the request came in */}
                              <TableCell className="py-3" title={r.created_at ? new Date(r.created_at).toLocaleString() : undefined}>
                                <div className="text-xs font-medium text-foreground">{time(r.created_at)}</div>
                                {r.created_at && (
                                  <div className="text-[11px] text-muted-foreground">
                                    {new Date(r.created_at).toLocaleDateString([], { day: 'numeric', month: 'short' })}
                                  </div>
                                )}
                              </TableCell>

                              {/* Who let this booking in; a name always beats echoing the Status column back at the reader. */}
                              <TableCell className="py-3 text-xs text-muted-foreground">
                                {r.approved_by_name ? (
                                  (r.booking_source === 'RECEPTION' || r.booking_source === 'PHONE') && !r.approved_at ? (
                                    /* A staff-entered booking reads "created", not "approved" - taking it down IS the decision. */
                                    <span title={r.created_at ? new Date(r.created_at).toLocaleString() : undefined}>
                                      <span className="inline-flex items-center gap-1">
                                        <UserCheck size={13} /> <strong className="text-foreground">{r.approved_by_name}</strong>
                                      </span>
                                      <span className="block text-[11px] mt-0.5">Created the booking</span>
                                    </span>
                                  ) : (
                                    /* The name answers who, the time answers when; older rows have no time recorded and omit it. */
                                    <span title={r.approved_at ? new Date(r.approved_at).toLocaleString() : undefined}>
                                      <span className="inline-flex items-center gap-1">
                                        <UserCheck size={13} /> <strong className="text-foreground">{r.approved_by_name}</strong>
                                      </span>
                                      <span className="block text-[11px] mt-0.5">
                                        Approved{r.approved_at && ` · ${new Date(r.approved_at).toLocaleString([], {
                                          day: 'numeric', month: 'short',
                                          hour: '2-digit', minute: '2-digit',
                                        })}`}
                                      </span>
                                    </span>
                                  )
                                ) : r.auto_approved ? (
                                  <span className="inline-flex items-center gap-1 text-emerald-700"
                                    title={r.approved_at ? new Date(r.approved_at).toLocaleString() : undefined}>
                                    <BadgeCheck size={13} /> Auto-approved
                                  </span>
                                ) : r.reservation_status === 'BOOKED' ? (
                                  <span className="inline-flex items-center gap-1 text-amber-700">
                                    <Clock size={13} /> Awaiting approval
                                  </span>
                                ) : r.reservation_status === 'CANCELLED' || r.reservation_status === 'NO_SHOW' ? (
                                  /* Ended before anyone decided it: no person to name, and the Status column already says how. */
                                  <span title="Nobody approved this booking — it ended while still undecided">—</span>
                                ) : r.booking_source === 'ONLINE' ? (
                                  <span className="inline-flex items-center gap-1 text-emerald-700">
                                    <BadgeCheck size={13} /> Confirmed
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1">
                                    <UserCheck size={13} /> Booked by staff
                                  </span>
                                )}
                              </TableCell>

                              {/* Actions are right-aligned, so the buttons form their own column edge down the page. */}
                              <TableCell className="py-3">
                                <div className="flex flex-wrap items-center justify-end gap-1.5">
                                  {/* Approve / decline a pending request */}
                                  {/* Approve honours whatever the Table column's picker is set to, else it keeps the requested table. */}
                                  {r.reservation_status === 'BOOKED' && (
                                    <>
                                      <button
                                        type="button"
                                        disabled={decidingId === r.id}
                                        onClick={() => decide(r, 'approve')}
                                        className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-xs font-semibold px-3 py-1.5"
                                        title={r.table_code ? `Approve — keeps table ${r.table_code}` : 'Approve this booking'}
                                      >
                                        <Check size={13} /> Approve
                                      </button>
                                      <button
                                        type="button"
                                        disabled={decidingId === r.id}
                                        onClick={() => decide(r, 'decline')}
                                        className="inline-flex items-center gap-1.5 rounded-md border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-60 text-xs font-semibold px-3 py-1.5"
                                      >
                                        <X size={13} /> Decline
                                      </button>
                                    </>
                                  )}

                                  {r.reservation_status === 'CONFIRMED' && (
                                    <>
                                      {/* Check in seats the guest and fires any pre-order; an early guest is seated by moving the booking. */}
                                      <button
                                        type="button"
                                        disabled={checkingInId === r.id || tooEarly}
                                        onClick={() => checkIn(r)}
                                        className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed text-white text-xs font-semibold px-3 py-1.5"
                                        title={tooEarly
                                          ? `Check-in opens at ${time(opensAt.toISOString())} (${EARLY_CHECKIN_MINUTES} min before the booking) — guest already here? Use Change time`
                                          : r.has_preorder
                                            ? 'Seat the guest — their pre-order goes to the kitchen now'
                                            : 'Seat the guest at their booked table'}
                                      >
                                        <LogIn size={13} />
                                        {checkingInId === r.id ? 'Checking in…' : 'Check in'}
                                      </button>
                                      {/* Extend the hold, only from the booked time, since there is no hold running before it. */}
                                      {started && (
                                      <button
                                        type="button"
                                        disabled={extendBusy}
                                        onClick={() => {
                                          setExtendOpenId(extendOpenId === r.id ? null : r.id);
                                          setCustomMins('');
                                        }}
                                        className="inline-flex items-center gap-1.5 rounded-md border border-sky-200 text-sky-700 hover:bg-sky-50 disabled:opacity-60 text-xs font-semibold px-3 py-1.5"
                                        title="Keep the table held longer — recorded in the booking's history"
                                      >
                                        <Timer size={13} /> Extend hold
                                      </button>
                                      )}
                                      {/* Cancel a confirmed booking (guest called it off, etc.) */}
                                      <button
                                        type="button"
                                        disabled={decidingId === r.id}
                                        onClick={() => decide(r, 'cancel')}
                                        className="inline-flex items-center gap-1.5 rounded-md border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-60 text-xs font-semibold px-3 py-1.5"
                                      >
                                        <X size={13} /> Cancel
                                      </button>
                                      {/* No show is not Cancel: it goes on the guest's profile, so it is its own button and only from the booked time. */}
                                      {started && (
                                      <button
                                        type="button"
                                        disabled={decidingId === r.id}
                                        onClick={() => decide(r, 'no-show')}
                                        className="inline-flex items-center gap-1.5 rounded-md border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-60 text-xs font-semibold px-3 py-1.5"
                                        title="The party never arrived — releases the table and counts on the guest's profile"
                                      >
                                        <UserX size={13} /> No show
                                      </button>
                                      )}
                                    </>
                                  )}

                                  {/* History — the booking's audit trail on demand */}
                                  <button
                                    type="button"
                                    onClick={() => toggleAudit(r)}
                                    className={`inline-flex items-center gap-1 text-xs font-medium underline-offset-2 hover:underline ${
                                      auditOpenId === r.id ? 'text-foreground' : 'text-muted-foreground'
                                    }`}
                                    title="Every decision made on this booking — approvals, hold extensions, cancellation or no-show"
                                  >
                                    <History size={13} /> History
                                  </button>
                                </div>
                              </TableCell>
                            </TableRow>

                            {/* Anything of unpredictable height lives here, spanning the full width, so no cell has to stretch. */}
                            {hasSub && (
                              <TableRow className="hover:bg-transparent">
                                <TableCell colSpan={12} className="pt-0 pb-3">
                                  {r.notes && (
                                    <div className="text-xs text-amber-700 mb-2">“{r.notes}”</div>
                                  )}

                                  {/* Change-time panel; "Now" is the early-guest shortcut that then lights up Check in. */}
                                  {timeOpenId === r.id && ['BOOKED', 'CONFIRMED'].includes(r.reservation_status) && (
                                    <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-secondary/30 px-3 py-2 mb-2">
                                      <span className="text-xs text-muted-foreground">Move this booking to…</span>
                                      {/* Date and clock as separate controls, with `newTime` staying one "YYYY-MM-DDTHH:MM" string. */}
                                      <input
                                        type="date"
                                        value={newTime.slice(0, 10)}
                                        min={toLocalInput().slice(0, 10)}
                                        onChange={(e) => setNewTime(
                                          `${e.target.value}T${newTime.slice(11) || '19:00'}`)}
                                        className="h-7 rounded-md border border-input bg-background px-2 text-xs"
                                      />
                                      <TimePicker
                                        value={newTime.slice(11)}
                                        step={15}
                                        onChange={(v) => setNewTime(
                                          `${newTime.slice(0, 10) || toLocalInput().slice(0, 10)}T${v}`)}
                                      />
                                      <button
                                        type="button"
                                        disabled={timeBusy}
                                        onClick={() => setNewTime(toLocalInput())}
                                        className="rounded-md border border-border bg-background px-2.5 py-1 text-xs font-medium hover:bg-slate-50 disabled:opacity-60"
                                        title="The guest is standing here — move the booking to right now"
                                      >
                                        Now — guest is here
                                      </button>
                                      <button
                                        type="button"
                                        disabled={timeBusy || !newTime}
                                        onClick={() => reschedule(r)}
                                        className="rounded-md bg-primary text-primary-foreground px-2.5 py-1 text-xs font-semibold disabled:opacity-50"
                                      >
                                        {timeBusy ? 'Moving…' : 'Move booking'}
                                      </button>
                                      <span className="text-[11px] text-muted-foreground">
                                        Check-in opens {EARLY_CHECKIN_MINUTES} min before the booked time; the move is
                                        recorded in the booking's history.
                                      </span>
                                    </div>
                                  )}

                                  {/* Extend-hold panel: quick grants + a custom number */}
                                  {extendOpenId === r.id && r.reservation_status === 'CONFIRMED' && (
                                    <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-secondary/30 px-3 py-2">
                                      <span className="text-xs text-muted-foreground">Keep the table held for another…</span>
                                      {[10, 15, 30].map((m) => (
                                        <button
                                          key={m}
                                          type="button"
                                          disabled={extendBusy}
                                          onClick={() => extendHold(r, m)}
                                          className="rounded-md border border-border bg-background px-2.5 py-1 text-xs font-medium hover:bg-slate-50 disabled:opacity-60"
                                        >
                                          +{m} min
                                        </button>
                                      ))}
                                      <input
                                        type="number"
                                        min="1"
                                        value={customMins}
                                        onChange={(e) => setCustomMins(e.target.value)}
                                        placeholder="Custom"
                                        className="h-7 w-20 rounded-md border border-input bg-background px-2 text-xs"
                                      />
                                      <button
                                        type="button"
                                        disabled={extendBusy || !Number(customMins)}
                                        onClick={() => extendHold(r, customMins)}
                                        className="rounded-md bg-primary text-primary-foreground px-2.5 py-1 text-xs font-semibold disabled:opacity-50"
                                      >
                                        Add
                                      </button>
                                      <span className="text-[11px] text-muted-foreground">
                                        The extension is recorded in the booking's history.
                                      </span>
                                    </div>
                                  )}

                                  {/* Audit trail */}
                                  {auditOpenId === r.id && (
                                    <div className="rounded-md border border-border bg-secondary/30 px-3 py-2 mt-2">
                                      {auditLoading === r.id ? (
                                        <p className="text-xs text-muted-foreground">Loading history…</p>
                                      ) : !(audit[r.id] || []).length ? (
                                        <p className="text-xs text-muted-foreground">
                                          No decisions recorded on this booking yet.
                                        </p>
                                      ) : (
                                        <ul className="space-y-1">
                                          {audit[r.id].map((e, i) => (
                                            <li key={i} className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                                              <span className={`px-1.5 py-0.5 rounded-full font-medium ${AUDIT_CLS[e.event] || 'bg-secondary text-foreground'}`}>
                                                {AUDIT_LABEL[e.event] || (e.event === 'PAYMENT_EXPIRED' ? t('tables.audit.paymentExpired') : e.event === 'BLACKLISTED' ? t('tables.audit.blacklisted') : e.event)}
                                              </span>
                                              {auditDetail(e) && <span>{auditDetail(e)}</span>}
                                              {e.actor_name && (
                                                <span>by <strong className="text-foreground">{e.actor_name}</strong></span>
                                              )}
                                              {e.created_at && (
                                                <span>· {new Date(e.created_at).toLocaleString([], {
                                                  day: 'numeric', month: 'short',
                                                  hour: '2-digit', minute: '2-digit',
                                                })}</span>
                                              )}
                                            </li>
                                          ))}
                                        </ul>
                                      )}
                                    </div>
                                  )}
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        );
                      })}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
}
